import React, { useState } from 'react';
import './HelpModal.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'shortcuts' | 'usage'>('shortcuts');

  if (!isOpen) return null;

  const shortcuts = [
    { key: 'V', description: '선택 도구' },
    { key: 'R', description: '사각형 도구' },
    { key: 'E', description: '원형 도구' },
    { key: 'T', description: '텍스트 도구' },
    { key: 'Delete / Backspace', description: '선택된 객체 삭제' },
    { key: 'Ctrl/Cmd + C', description: '복사' },
    { key: 'Ctrl/Cmd + X', description: '오려두기' },
    { key: 'Ctrl/Cmd + V', description: '붙여넣기' },
    { key: 'Ctrl/Cmd + Z', description: '되돌리기' },
    { key: 'Ctrl/Cmd + Shift + Z', description: '다시실행' },
    { key: 'Ctrl/Cmd + Y', description: '다시실행' },
    { key: 'Ctrl/Cmd + Alt + Z', description: '다시실행' },
    { key: 'Space', description: '캔버스 패닝 (드래그하여 이동)' },
    { key: 'Shift', description: '스냅/비율 유지 (그리기 시 정사각형/정원)' },
    { key: 'Escape', description: '선택 해제' },
    { key: 'Ctrl/Cmd + Alt + G/C', description: '클리핑 마스크 토글' },
    { key: 'Ctrl/Cmd + Alt + O', description: '템플릿 불러오기' },
    { key: 'Ctrl/Cmd + Alt + T', description: '템플릿 저장' },
    { key: 'Ctrl/Cmd + Alt + N', description: '새 프로젝트' },
    { key: 'Ctrl/Cmd + ]', description: '레이어 순서 한 칸 앞으로 이동' },
    { key: 'Ctrl/Cmd + [', description: '레이어 순서 한 칸 뒤로 이동' },
    { key: 'Ctrl/Cmd + Alt + ]', description: '레이어 순서 맨 앞으로 이동' },
    { key: 'Ctrl/Cmd + Alt + [', description: '레이어 순서 맨 뒤로 이동' },
    { key: 'Ctrl/Cmd + Alt + L', description: '이미지 레이어 잠금/해제 토글' },
    { key: '드래그 + Alt (이미지 파일)', description: '이미지 객체에 이미지 파일을 드래그하여 단일 이미지 교체' },
    { key: '드래그 + Ctrl/Cmd + Alt (이미지 파일)', description: '동일한 이미지를 사용하는 모든 객체에 이미지 파일을 드래그하여 일괄 교체' },
    { key: '드래그 + Alt (ZIP 파일)', description: 'ZIP 파일을 아트보드 위로 드래그하여 해당 아트보드만 템플릿으로 교체' },
    { key: '드래그 (ZIP 파일)', description: 'ZIP 파일을 캔버스로 드래그하여 전체 템플릿 불러오기' },
    { key: 'Ctrl/Cmd + Shift + S', description: 'PNG 저장 (원본 해상도)' },
    { key: 'Ctrl/Cmd + Shift + Alt + S', description: '썸네일 저장 (30% 해상도)' },
    { key: '방향키 (↑↓←→)', description: '객체 이동 (1px)' },
    { key: 'Shift + 방향키', description: '객체 이동 (10px)' },
  ];

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>도움말</h2>
          <button className="help-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="help-modal-tabs">
          <button
            className={`help-modal-tab ${activeTab === 'shortcuts' ? 'active' : ''}`}
            onClick={() => setActiveTab('shortcuts')}
          >
            단축키
          </button>
          <button
            className={`help-modal-tab ${activeTab === 'usage' ? 'active' : ''}`}
            onClick={() => setActiveTab('usage')}
          >
            사용법
          </button>
        </div>

        <div className="help-modal-body">
          {activeTab === 'shortcuts' && (
            <div className="help-shortcuts">
              <h3>키보드 단축키</h3>
              <div className="shortcuts-list">
                {shortcuts.map((shortcut, index) => (
                  <div key={index} className="shortcut-item">
                    <div className="shortcut-key">{shortcut.key}</div>
                    <div className="shortcut-description">{shortcut.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="help-usage">
              <h3>주요 기능 사용법</h3>
              
              <section className="usage-section">
                <h4>1. 객체 생성</h4>
                <ul>
                  <li>상단 도구 모음에서 원하는 도구를 선택하거나 단축키를 사용하세요.</li>
                  <li>사각형/원형 도구: 캔버스에서 드래그하여 객체를 그립니다.</li>
                  <li>Shift 키를 누른 채로 드래그하면 정사각형/정원을 그릴 수 있습니다.</li>
                  <li>텍스트 도구: 캔버스를 클릭하여 텍스트를 입력합니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>2. 객체 선택 및 편집</h4>
                <ul>
                  <li>선택 도구(V)로 객체를 클릭하여 선택합니다.</li>
                  <li>Ctrl/Cmd 키를 누른 채로 여러 객체를 클릭하면 다중 선택이 가능합니다.</li>
                  <li>선택된 객체는 모서리에 핸들이 표시되며, 드래그하여 크기를 조정할 수 있습니다.</li>
                  <li>오른쪽 패널에서 위치, 크기, 색상, 스타일 등을 세밀하게 조정할 수 있습니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>3. 객체 이동 및 변형</h4>
                <ul>
                  <li>선택된 객체를 드래그하여 이동할 수 있습니다.</li>
                  <li>방향키를 사용하여 1px씩 이동하고, Shift + 방향키로 10px씩 이동합니다.</li>
                  <li>객체 모서리의 핸들을 드래그하여 크기를 조정합니다.</li>
                  <li>객체 위쪽의 회전 핸들을 드래그하여 회전시킬 수 있습니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>4. 레이어 관리</h4>
                <ul>
                  <li>왼쪽 패널의 레이어 목록에서 모든 객체를 확인할 수 있습니다.</li>
                  <li>레이어를 드래그하여 순서를 변경할 수 있습니다 (위로 드래그하면 앞으로, 아래로 드래그하면 뒤로).</li>
                  <li>레이어 이름을 더블클릭하여 편집할 수 있습니다.</li>
                  <li>단축키로도 레이어 순서를 변경할 수 있습니다. (Ctrl/Cmd + [/], Ctrl/Cmd + Alt + [/])</li>
                  <li>이미지 레이어는 자물쇠 아이콘 또는 Ctrl/Cmd + Alt + L로 잠금/해제할 수 있으며, 잠금된 이미지는 Alt 드래그 이미지 교체 대상에서 제외됩니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>5. 클리핑 마스크</h4>
                <ul>
                  <li>객체를 선택한 후 Ctrl/Cmd + Alt + G/C를 눌러 클리핑 마스크를 설정합니다.</li>
                  <li>클리핑 마스크가 설정된 객체는 부모 객체의 영역 내에서만 보입니다.</li>
                  <li>레이어 목록에서 클리핑 마스크 아이콘으로 관계를 확인할 수 있습니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>6. 색상 관리</h4>
                <ul>
                  <li>객체를 선택하지 않으면 오른쪽 패널에 "사용된 색상" 섹션이 표시됩니다.</li>
                  <li>사용된 색상을 클릭하여 일괄 변경할 수 있습니다.</li>
                  <li>스포이드 아이콘을 클릭하여 화면에서 색상을 추출할 수 있습니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>7. 아트보드 작업</h4>
                <ul>
                  <li>아트보드를 선택하면 오른쪽 패널에서 이름을 설정할 수 있습니다.</li>
                  <li>아트보드 내의 객체만 내보내기에 포함됩니다.</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>8. 템플릿 관리</h4>
                <ul>
                  <li>템플릿 불러오기: 저장된 템플릿 ZIP 파일을 불러와 프로젝트를 복원합니다. (단축키: Ctrl/Cmd + Alt + O)</li>
                  <li>템플릿 저장: 전체 프로젝트를 ZIP 파일로 저장하여 나중에 불러올 수 있습니다. (단축키: Ctrl/Cmd + Alt + T)</li>
                </ul>
              </section>

              <section className="usage-section">
                <h4>9. 내보내기</h4>
                <ul>
                  <li>하단 메뉴에서 "썸네일 저장", "PNG 저장" 또는 "템플릿 저장"을 선택합니다.</li>
                  <li>PNG 저장: 현재 아트보드를 원본 해상도로 이미지로 저장합니다. (단축키: Ctrl/Cmd + Shift + S)<br />(선택된 객체가 있으면 객체가 속해있는 아트보드만 저장됩니다.)</li>
                  <li>썸네일 저장: 현재 아트보드를 60% 해상도로 축소하여 JPG 품질 75%로 썸네일 이미지로 저장합니다. 파일명에 "thumb_" 접두사가 자동으로 추가됩니다. (단축키: Ctrl/Cmd + Shift + Alt + S)</li>
                </ul>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

